import React, { useEffect } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { gql } from '@apollo/client';
import { Stack, SelectableButton, useBreakpoint, Overlay } from '@moda/om';
import { GiftFinderNavigationFragment } from '../../generated/types';
import { hrefFor } from '../../routers';

import './GiftFinderNavigation.scss';

export const GIFT_FINDER_NAVIGATION_FRAGMENT = gql`
  fragment GiftFinderNavigationFragment on GiftCategory {
    slug
    label
    children {
      slug
      label
    }
  }
`;

type GiftFinderNavigationProps = {
  categories: GiftFinderNavigationFragment[];
  isLoading: boolean;
};

const usePreserveScrollPosition = () => {
  const location = useLocation();

  useEffect(() => {
    const savedPosition = sessionStorage.getItem('scrollPosition');
    if (savedPosition) {
      setTimeout(() => window.scrollTo(0, parseInt(savedPosition, 10)), 0);
    }

    return () => {
      const scrollY = window.scrollY;
      sessionStorage.setItem('scrollPosition', scrollY.toString());
    };
  }, [location.pathname]);
};

export const GiftFinderNavigation: React.FC<GiftFinderNavigationProps> = ({
  categories,
  isLoading
}) => {
  usePreserveScrollPosition();

  const { giftCategory = '', giftSubcategory } = useParams<{
    giftCategory?: string;
    giftSubcategory?: string;
  }>();

  const isMobile = useBreakpoint({ lt: 'md' }).matches;

  const currentSubcategories =
    categories.find(category => category.slug === giftCategory)?.children ?? [];

  const previousSubcategories =
    categories.find(category => category.children.length > 0)?.children ?? [];

  const subcategories =
    currentSubcategories.length > 0 ? currentSubcategories : previousSubcategories;

  const showOverlay = isLoading && currentSubcategories.length === 0;

  if (categories.length === 0) return;

  return (
    <Stack space={0}>
      <Stack
        className="GiftFinderNavigation__primary"
        direction="horizontal"
        justifyContent={isMobile ? 'left' : 'center'}
        space={3}
      >
        {categories.map(category => (
          <SelectableButton
            key={category.slug}
            className="GiftFinderNavigation__primary-button"
            to={hrefFor.GiftFinderPage(category.slug ? { giftCategory: category.slug } : undefined)}
            selected={category.slug === giftCategory}
          >
            {category.label}
          </SelectableButton>
        ))}
      </Stack>
      {subcategories.length > 0 && (
        <Stack
          className="GiftFinderNavigation__secondary"
          direction="horizontal"
          justifyContent={isMobile ? 'left' : 'center'}
          space={3}
        >
          {subcategories.map(subcategory => (
            <SelectableButton
              key={subcategory.slug}
              className="GiftFinderNavigation__secondary-button"
              to={hrefFor.GiftFinderPage({
                ...(giftCategory ? { giftCategory } : {}),
                giftSubcategory: subcategory.slug
              })}
              selected={subcategory.slug === giftSubcategory}
            >
              {subcategory.label}
            </SelectableButton>
          ))}
          {showOverlay && <Overlay />}
        </Stack>
      )}
    </Stack>
  );
};
